import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment, Button, Message } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

import {
  updateRezept,
  getRezeptByID,
  deleteRezept,
  deleteRezeptForce,
  checkRezepte,
  getBezuege,
  getZutatengruppen,
  getRohstoffeNames,
  getProdukteNames,
  getGrundrezepte,
  getRezeptkategorien,
  getRezeptgruppen,
  getVistPermissions,
} from '../../../actions';

import UnsavedChangesWarning from '../../../helpers/UnsavedChangesWarning';
import convertNumbersToStrings from '../../../helpers/convertNumbersToStrings';
import {
  canCrudThisRezept,
  canEditInternExtern,
} from '../../../helpers/permissions';

import ButtonArea from '../ButtonArea';

import RezeptForm from './RezeptForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RezeptEdit = ({
  pathname,
  rezept,
  rezept_loaded,
  getRezeptByID,
  updateRezept,
  deleteRezept,
  deleteRezeptForce,
  getBezuege,
  bezuege,
  getZutatengruppen,
  zutatengruppen,
  getRohstoffeNames,
  rohstoffeNames,
  getProdukteNames,
  produkteNames,
  getGrundrezepte,
  grundrezepte,
  getRezeptkategorien,
  rezeptkategorien,
  getRezeptgruppen,
  rezeptgruppen,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    bezeichnung: '',
    rezeptnummer: '',
    rezeptcode: '',
    kennung: '',
    variante: 0,
    intern_extern: '',
    bezuege_id: '',
    brennwert_kj: '',
    brennwert_kcal: '',
    eiweiss: '',
    kohlenhydrate: '',
    zucker: '',
    fett: '',
    ges_fettsaeuren: '',
    ballaststoffe: '',
    natrium: '',
    staerke: '',
    rezeptbeschreibung_lang: '',
    rezeptbeschreibung_kurz: '',
    basis_euro: '',
    zusatzinformationen: '',
    is_import: 0,
    image_uuid: '',
    status: 1,
    aktuell: null,
    kategorien: [],
    gruppen: [],
    image: {},
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [initialZutaten, setInitialZutaten] = useState({});

  const [zutaten, setZutaten] = useState([]);
  const [zutatenToDelete, setZutatenToDelete] = useState([]);

  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isIncompleteZutat, setIsIncompleteZutat] = useState(false);
  const [isRezeptnummerDuplicate, setIsRezeptNummerDuplicate] = useState(false);
  const [backURL, setBackURL] = useState(null);
  const [modified, setModified] = useState(false);

  const isSubmitDisabled =
    isRezeptnummerDuplicate ||
    formData.name === '' ||
    formData.verkehrsbezeichnung === '' ||
    formData.kennung === '' ||
    formData.rezeptcode === '' ||
    formData.variante === '' ||
    formData.bezuege_id === '' ||
    formData.gruppen.length === 0 ||
    isIncompleteZutat ||
    modified === false;

  // dispatch for rezepteCheck()
  const dispatch = useDispatch();
  // rezepteCheck action
  const rezepteCheck = useSelector((state) => state.rezepteCheck);

  // assemble rezeptnummer from formdata
  const formData_rezeptnummer =
    formData.kennung + '-' + formData.rezeptcode + '-' + formData.variante;

  // assemble rezeptnummer from formdata without kennung letter
  const formData_rezeptnummer_unique =
    formData.rezeptcode + '-' + formData.variante;

  // last edited field
  const [lastField, setLastField] = useState('');

  const history = useHistory();
  // Edit/delete are gated per-recipe: at least one of THIS recipe's
  // Rezeptgruppen must be in the user's CRUD set (mirrors backend
  // ensure_can_crud_existing_rezept). Without this, e.g. MK navigating
  // directly to the edit URL of a Grundrezepte-only recipe would see
  // the form and only fail at save time.
  const canEdit = canCrudThisRezept(permissions, rezept);
  const showDeleteButton = canEdit;
  // Intern/Extern is restricted to Admin + F&E per TASK-90 §3.
  // FB/PI/MK/FV see the field but cannot change it; the backend
  // also strips intern_extern from PATCH payloads as a safety net.
  const canEditInternExternField = canEditInternExtern(permissions);
  // Restrict the Rezeptgruppe picker to groups the user can CRUD —
  // backend PATCH re-validates via validate_rezeptgruppen_assignment.
  const allowedRezeptgruppen = useMemo(() => {
    const allowed = permissions?.rezeptgruppen_crud;
    if (!rezeptgruppen) return [];
    if (!allowed) return rezeptgruppen;
    return rezeptgruppen.filter((rg) => allowed.includes(rg.name));
  }, [rezeptgruppen, permissions]);
  // flag to make sure permissions are fully loaded before returning
  // unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    setBackURL(`/controlpanel/rezeptdatenbank/rezepte/details?id=${id}`);
    getRezeptByID(id);
    getBezuege({ getAll: true });
    getZutatengruppen({ getAll: true });
    getRohstoffeNames();
    getProdukteNames();
    getGrundrezepte();
    getRezeptkategorien({ getAll: true });
    getRezeptgruppen({ getAll: true });
    // eslint-disable-next-line
  }, [getRezeptByID, getBezuege]);

  useEffect(() => {
    if (rezept) {
      const rezeptdata = {
        rezeptcode: rezept.rezeptcode,
        variante: rezept.variante,
        bezuege_id: rezept.bezuege_id,
        kopie_von: rezept.kopie_von, //needed?
        mehrzweckfeld: rezept.mehrzweckfeld, //needed?
        name: rezept.name,
        name_etikett: rezept.name_etikett,
        preis: rezept.preis,
        rezeptstatus: rezept.rezeptstatus,
        verkehrsbezeichnung: rezept.verkehrsbezeichnung,
        zusatz: rezept.zusatz,
        intern_extern: rezept.intern_extern,
        aktuell: rezept.aktuell,
        knetzeit: rezept.knetzeit,
        ruehrzeit: rezept.ruehrzeit,
        teigtemperatur: rezept.teigtemperatur,
        massentemperatur: rezept.massentemperatur,
        teigruhe: rezept.teigruhe,
        teigausbeute: rezept.teigausbeute,
        quellzeit: rezept.quellzeit,
        pressen_zwischengare: rezept.pressen_zwischengare,
        backtemperatur: rezept.backtemperatur,
        siedefetttemperatur: rezept.siedefetttemperatur,
        kerntemperatur: rezept.kerntemperatur,
        backzeit: rezept.backzeit,
        herstellungstext: rezept.herstellungstext,
        stueckzahl: rezept.stueckzahl,
        teigeinlage: rezept.teigeinlage,
        pressengewicht: rezept.pressengewicht,
        masseneinwaage: rezept.masseneinwaage,
        backverlust: rezept.backverlust,
        kennung: rezept.kennung,
        etikett: rezept.etikett,
        kategorien: rezept.kategorien,
        gruppen: rezept.gruppen?.filter((g) =>
          allowedRezeptgruppen?.some((allowedGroup) => allowedGroup.id === g),
        ),
        image: rezept.image,
        image_uuid: rezept.image_uuid,
      };
      setFormData(rezeptdata);
      setInitialFormData(convertNumbersToStrings(rezeptdata));
      setZutaten(rezept.zutaten);
      setInitialZutaten(
        rezept?.zutaten?.map((zutat) => convertNumbersToStrings(zutat)),
      );
    }
    // eslint-disable-next-line
  }, [rezept]);

  useEffect(() => {
    if (zutaten) {
      if (!zutaten.every((zutat) => zutat.hasOwnProperty('kategorie'))) {
        const updatedZutaten = zutaten.map((zutat) => {
          if (zutat.grundrezepte_id !== 0) {
            zutat.kategorie = 'grundrezept';
          } else if (zutat.rohstoffe_id !== 0) {
            zutat.kategorie = 'rohstoff';
          } else if (zutat.produkte_id !== 0) {
            zutat.kategorie = 'produkt';
          }
          return zutat;
        });

        setZutaten(updatedZutaten);
      }
      setIsIncompleteZutat(
        zutaten.some((zutat) => {
          return (
            (!zutat.rohstoffe_id &&
              !zutat.produkte_id &&
              !zutat.grundrezepte_id) ||
            ((zutat.rohstoffe_id === '0' || zutat.rohstoffe_id === 0) &&
              (zutat.produkte_id === '0' || zutat.produkte_id === 0) &&
              (zutat.grundrezepte_id === '0' || zutat.grundrezepte_id === 0)) ||
            isNaN(parseFloat(zutat.menge)) ||
            zutat.menge === 0 ||
            zutat.menge === '0' ||
            zutat.zutatengruppen_id === 0 ||
            zutat.zutatengruppen_id === '0'
          );
        }),
      );
    }
  }, [zutaten]);

  //check if Form values have changed, to warn user form leaving the page
  useEffect(() => {
    if (!isEqual(convertNumbersToStrings(formData), initialFormData)) {
      setModified(true);
    } else {
      setModified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    const normalized_zutaten = zutaten?.map(({ updated, kategorie, ...rest }) =>
      convertNumbersToStrings(rest),
    );
    if (!isEqual(normalized_zutaten, initialZutaten)) {
      setModified(true);
    } else {
      setModified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zutaten]);

  const addZutat = (e) => {
    e.preventDefault();
    setZutaten([
      ...zutaten,
      {
        kategorie: 'rohstoff',
        rezepte_id: '0',
        rohstoffe_id: '0',
        produkte_id: '0',
        zutatengruppen_id: '0',
        grundrezepte_id: '0',
        sorting: zutaten.length,
        info: '',
        menge: '',
        backen: 2, // 1 = true 2= false!!
        select_zut_grundr: 1, // what does this do?
      },
    ]);
  };

  const removeZutat = (e, index) => {
    e.preventDefault();

    if (zutaten[index].id) {
      setZutatenToDelete([zutaten[index].id, ...zutatenToDelete]);
    }

    const updatedZutaten = zutaten.filter((_, i) => i !== index);
    updatedZutaten.forEach((zutat, index) => {
      zutat.sorting = index;
    });
    setZutaten(updatedZutaten);
  };

  /*
   * `handleZutatChange` is fundamentally flawed.
   * It cannot be called multiple times within the same render, since each call overwrites
   * the previous one and only the last change takes effect.
   *
   * This issue arises from a conceptual misunderstanding of React’s rendering process. Fixing
   * it would require substantial refactoring across many components.
   *
   * For now, I’m leaving it as-is, hoping it won’t cause issues (though it inevitably will
   * if invoked more than once).
   *
   * The current solution is to pass down setZutaten, which is the idiomatic React approach.
   * However, keep in mind that this bypasses all the processing handled by updateZutat,
   * so it is intended solely for drag-and-drop reordering.
   */

  const handleZutatChange = (e, index) => {
    const { name, value } = e.target;

    const updatedZutat = { ...zutaten[index] };

    if (e.type === 'click') {
      if (updatedZutat.backen !== 1) {
        updatedZutat.backen = 1;
      } else if (updatedZutat.backen === 1) {
        updatedZutat.backen = 0;
      }
    } else {
      updatedZutat[name] = value;
    }

    if (name === 'kategorie') {
      updatedZutat.rohstoffe_id = '0';
      updatedZutat.produkte_id = '0';
      updatedZutat.grundrezepte_id = '0';
    }

    if (updatedZutat.id) {
      updatedZutat.updated = true;
    }

    const updatedZutatenList = zutaten.filter((_, i) => i !== index);
    updatedZutatenList.splice(updatedZutat.sorting, 0, updatedZutat);
    updatedZutatenList.forEach((zutat, index) => {
      zutat['sorting'] = index;
    });
    setZutaten(updatedZutatenList);
  };

  const handleKategorienChange = (value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      kategorien: value,
    }));
  };

  const handleGruppenChange = (value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      gruppen: value ? [value] : [],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLastField(name);

    if (name === 'intern_extern') {
      // For intern/extern field, convert value to a boolean
      const isIntern = value === 'intern';
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: isIntern,
      }));
    } else if (name === 'kennung') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value.toUpperCase(),
      }));
    } else if (name === 'rezeptcode') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value.replace(/\D/g, ''),
      }));
    } else if (name === 'variante') {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: Math.min(parseInt(value), 99),
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }

    let kennung = name === 'kennung' ? value : formData.kennung;
    let rezeptcode = name === 'rezeptcode' ? value : formData.rezeptcode;
    let variante = name === 'variante' ? value : formData.variante || 0;

    let rezeptnummer = `${kennung}-${rezeptcode}-${variante}`;

    dispatch(checkRezepte('rezeptnummer', rezeptnummer, rezeptnummer));
    setIsRezeptNummerDuplicate(
      rezepteCheck.subrequests?.[rezeptnummer]?.data.value_exists,
    );
    // }
  };

  // set variante to lowest available variante if rezeptcode is already in use
  // set isRezeptNummerDuplicate to true if the user changes to an already existing variant
  useEffect(() => {
    // only do this if there is data for the suggestions
    if (
      rezepteCheck.subrequests[formData_rezeptnummer]?.data?.suggested_variants
        ?.length > 0
    ) {
      // extract lowest possible variant
      let new_variante =
        rezepteCheck.subrequests[formData_rezeptnummer].data
          .suggested_variants[0];

      // extract if the current rezeptnummer is a duplicate
      let isDuplicate =
        rezepteCheck.subrequests[formData_rezeptnummer].data.value_exists;

      if (
        formData_rezeptnummer_unique ===
        `${rezept.rezeptcode}-${rezept.variante}`
      ) {
        setIsRezeptNummerDuplicate(false);
      } else if (new_variante !== undefined && lastField === 'rezeptcode') {
        // set variant to lowest possible
        setFormData((prevFormData) => ({
          ...prevFormData,
          variante: new_variante,
        }));
        // set duplicate flag to false since the new one is not used
        setIsRezeptNummerDuplicate(false);
      } else if (isDuplicate !== undefined && lastField === 'variante') {
        // if user changes variante set duplicate flag to value from endpoint
        setIsRezeptNummerDuplicate(isDuplicate);
      }

      if (
        lastField === 'rezeptcode' &&
        formData.rezeptcode === rezept.rezeptcode
      ) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          variante: rezept.variante,
        }));
        setIsRezeptNummerDuplicate(false);
      }
    }
    // eslint-disable-next-line
  }, [rezepteCheck.subrequests]);

  const handleSubmit = async (e) => {
    setModified(false);
    e.preventDefault();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    try {
      // Build zutaten_operations for atomic update
      const zutatenOperations = {
        create: [],
        update: [],
        delete: zutatenToDelete,
      };

      zutaten.forEach((zutat) => {
        if (zutat.id && zutat.updated) {
          // Existing zutat that was modified - add to update list
          zutatenOperations.update.push(zutat);
        } else if (!zutat.id) {
          // New zutat - add to create list
          zutatenOperations.create.push(zutat);
        }
      });

      // Send single request with all changes (rezept + zutaten operations)
      const payload = {
        ...formData,
        zutaten_operations: zutatenOperations,
      };

      const result = await updateRezept(id, payload);
      const updatedId = result.id;

      if (updatedId) {
        history.push(
          `/controlpanel/rezeptdatenbank/rezepte/details?id=${updatedId}`,
        );
      } else {
        setError('Es ist etwas schiefgelaufen, Rezept wurde nicht bearbeitet.');
      }
    } catch (error) {
      setError(
        error.name +
          ': Es ist etwas schiefgelaufen, Rezept wurde nicht bearbeitet',
      );
      setErrorDetail(error.name + ': ' + error.message);
    }
  };

  const handleDelete = async (options = {}) => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    const { force } = options;
    try {
      if (options) {
        await deleteRezeptForce(id);
      } else {
        await deleteRezept(id);
      }
      setDeleteSuccess(true);
    } catch (error) {
      if (error.response?.body?.error?.type === 'Referenced by rezepte') {
        setError(
          'Kann nicht gelöscht werden. ' +
            'Wird referenziert von Rezepten ' +
            error.response?.body?.error?.message,
        );
      } else {
        // Handle delete error
        setError(error.name);
      }
    }
  };

  // Calculations:

  // Calculate gesamtgewicht_zutaten
  const gesamtgewicht_zutaten = zutaten?.reduce(
    (totalWeight, zutat) => totalWeight + parseFloat(zutat.menge || 0),
    0,
  );

  // Calculate gesamtgewicht_zutaten_not_baken
  const gesamtgewicht_zutaten_not_baken = zutaten
    ?.filter((zutat) => zutat.backen !== 1)
    ?.reduce(
      (totalWeight, zutat) => totalWeight + parseFloat(zutat.menge || 0),
      0,
    );

  // calculate stueckgewicht
  let stueckgewicht = 0;
  if (formData.stueckzahl !== 0) {
    stueckgewicht =
      (gesamtgewicht_zutaten - gesamtgewicht_zutaten_not_baken) /
      parseFloat(formData.stueckzahl);
  }

  // calculate gebaeckgewicht
  let gebaeckgewicht = 0;
  if (formData.backverlust < 100) {
    gebaeckgewicht = stueckgewicht * ((100 - formData.backverlust) / 100);
  }

  // calculate gesamt gebaeckgewicht
  let gesamt_gebaeckgewicht = 0;
  if (formData.stueckzahl !== 0) {
    gesamt_gebaeckgewicht =
      gebaeckgewicht + gesamtgewicht_zutaten_not_baken / formData.stueckzahl;
  }
  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && rezept_loaded && !canEdit) {
    return <Error error={{ status: 401 }} />;
  }

  return (
    <div className="view-wrapper">
      <Helmet title="Rezept Bearbeiten" />
      <UnsavedChangesWarning
        condition={modified}
        message="Sie haben das Rezept bearbeitet. Wenn Sie diese Seite verlassen gehen alle Änderungen verloren. Wollen sie die Seite wirklich verlassen?"
      />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1> {rezept && rezept.name} </h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={backURL}
                  showDeleteButton={showDeleteButton}
                  references={rezept?.rezepte_ids || []}
                  typeString={'das Rezept'}
                  isChangedToInactive={
                    formData?.status != initialFormData?.status &&
                    formData?.status === '2'
                  }
                />
              </div>
            </div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <div className="error-message">
                <h2>{error}</h2>
                <p>{errorDetail}</p>
              </div>
            )}
            {deleteSuccess ? (
              <Message positive>
                <Message.Header>Rezept wurde gelöscht</Message.Header>
                <Button
                  primary
                  as={Link}
                  to="/controlpanel/rezeptdatenbank/rezepte"
                >
                  Zurück zur Rezepte-Übersicht
                </Button>
              </Message>
            ) : (
              <>
                <div className="rdb edit form rezepte">
                  {rezept_loaded && (
                    <RezeptForm
                      formData={formData}
                      isRezeptnummerDuplicate={isRezeptnummerDuplicate}
                      handleInputChange={handleInputChange}
                      bezuege={bezuege}
                      zutaten={zutaten}
                      rohstoffeNames={rohstoffeNames}
                      produkteNames={produkteNames}
                      grundrezepte={grundrezepte}
                      zutatengruppen={zutatengruppen}
                      addZutat={addZutat}
                      isIncompleteZutat={isIncompleteZutat}
                      handleZutatChange={handleZutatChange}
                      setZutaten={setZutaten} // for drag and drop
                      removeZutat={removeZutat}
                      gesamtgewicht_zutaten={gesamtgewicht_zutaten}
                      stueckgewicht={stueckgewicht}
                      gebaeckgewicht={gebaeckgewicht}
                      gesamt_gebaeckgewicht={gesamt_gebaeckgewicht}
                      handleKategorienChange={handleKategorienChange}
                      selectedKategorien={formData.kategorien}
                      kategorien={rezeptkategorien}
                      handleGruppenChange={handleGruppenChange}
                      selectedGruppen={formData.gruppen}
                      gruppen={allowedRezeptgruppen}
                      codeSuggestions={
                        rezepteCheck.subrequests?.[formData_rezeptnummer]?.data
                          .suggested_codes
                      }
                      canEditInternExtern={canEditInternExternField}
                    />
                  )}
                </div>
              </>
            )}
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

RezeptEdit.propTypes = {
  pathname: PropTypes.string.isRequired,
  rezept: PropTypes.object,
  rezept_loaded: PropTypes.bool,
  bezuege: PropTypes.array.isRequired,
  getBezuege: PropTypes.func.isRequired,
  getRezeptByID: PropTypes.func.isRequired,
  updateRezept: PropTypes.func.isRequired,
  deleteRezept: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  getZutatengruppen: PropTypes.func.isRequired,
  zutatengruppen: PropTypes.array.isRequired,
  getRohstoffeNames: PropTypes.func.isRequired,
  rohstoffeNames: PropTypes.array.isRequired,
  getProdukteNames: PropTypes.func.isRequired,
  produkteNames: PropTypes.array.isRequired,
  getGrundrezepte: PropTypes.func.isRequired,
  grundrezepte: PropTypes.array.isRequired,
  getRezeptkategorien: PropTypes.func.isRequired,
  rezeptkategorien: PropTypes.array.isRequired,
  getRezeptgruppen: PropTypes.func.isRequired,
  rezeptgruppen: PropTypes.array.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      rezept: state.rezeptByID.data,
      rezept_loaded: state.rezeptByID.loaded,
      bezuege: state.bezuege.data.bezuege,
      zutatengruppen: state.zutatengruppen.data.zutatengruppen,
      rohstoffeNames: state.rohstoffeNames.data,
      produkteNames: state.produkteNames.data,
      grundrezepte: state.grundrezepte.data,
      rezeptkategorien: state.rezeptkategorien.data.rezeptkategorien,
      rezeptgruppen: state.rezeptgruppen.data.rezeptgruppen,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
    }),
    {
      updateRezept,
      getRezeptByID,
      deleteRezept,
      deleteRezeptForce,
      getBezuege,
      getZutatengruppen,
      getRohstoffeNames,
      getProdukteNames,
      getGrundrezepte,
      getRezeptkategorien,
      getRezeptgruppen,
      getVistPermissions,
    },
  ),
)(RezeptEdit);
